import { Library, initialState } from './library.state';
import { LibraryActions, LibraryActionType, LoadFilterStatus } from './library.actions';

import * as Objects from '@core/objects';
import * as Search from '@app/models/search';
import { VersionShareItem } from '@core/models/version-share.model';

export function libraryReducer(state = initialState, action: LibraryActions): Library {
    switch (action.type) {
		// EVENTS STATUS
        case LibraryActionType.Clear:
            return {
                ...state,
                available_filters: initialState.available_filters,
                search: initialState.search,
                search_query_params: initialState.search_query_params,
                results: initialState.results,
                versions_status_access: initialState.versions_status_access,
                version_feedbacks: initialState.version_feedbacks
            };

        case LibraryActionType.Init:
            if(state.starting.initialized)
                return state;

            let term = action.params!=null? action.params.term : "";
            return {...state, starting : {...state.starting, initialized: true}, 
                        search_query_params: action.params, 
                        search: {...state.search, term: term},
                        version_feedbacks: {...state.version_feedbacks, feedbackAccess: action.feedbackAccess}
                    };

        case LibraryActionType.LoadVersionsStatusAccess:
            return state; // nothing by now.

        case LibraryActionType.LoadVersionsStatusAccessSuccess:
            return {...state, versions_status_access: action.statusAccess};
        
        case LibraryActionType.LoadVersionsStatusAccessError:
            return {...state, versions_status_access: initialState.versions_status_access};


        case LibraryActionType.LoadFilterLabelsSuccess:
            return {...state, available_filters: {...state.available_filters, labels: {...state.available_filters.labels, items: action.labels }}};
        
        case LibraryActionType.LoadFilterLabelsError:
            return state;
        
        case LibraryActionType.LoadFilterStatusSuccess:
            return {...state, available_filters: {...state.available_filters, status: {...state.available_filters.status, items: action.status }}};
        
        case LibraryActionType.LoadFilterStatusError:
            return state;

        case LibraryActionType.PushSearchFilters:
            let filters = action.filters.filter((e: Search.SearchAppliedFilter) => !state.search.filters.some((ef: Search.SearchAppliedFilter)=> ef.key == e.key && ef.item.id == e.item.id));
            return {...state, search: {...state.search, filters: [...state.search.filters, ...filters]}};

        case LibraryActionType.ApplySearchFilter: 
            return {...state, search: {...state.search, filters: [...state.search.filters, action.filter] }};

        case LibraryActionType.RemoveSearchFilter: 
            return {...state, search: {...state.search, filters: state.search.filters.filter((f: Search.SearchAppliedFilter) => !(f.key == action.filter.key && f.item.id == action.filter.item.id)) }};
	    
        case LibraryActionType.SetSearchTerm:
            return {...state, search: {...state.search, term: action.term}};

        // case LibraryActionType.SetSearchQuery:
        //     // reset page also
        //     return {...state, search: {...state.search, query: action.query}, results: {...state.results, next_page: 1 }};
        
        case LibraryActionType.SetQueryParams:
            // reset page also
            return {...state, search_query_params: action.params, results: {...state.results, next_page: 1, status: Search.SearchHttpStatus.INITIAL }};
        // oct-2026: restored. Without LOADING the status stays DONE while a page loads, so every infinite scroll
        // event dispatched the same page again (and switchMap cancelled the previous request, still processed by the backend)
        case LibraryActionType.LoadResults:
            return {...state, results: {...state.results, status: Search.SearchHttpStatus.LOADING }};

        // back to the status before LoadResults, so a failed page can be retried by scrolling as before
        case LibraryActionType.LoadResultsError:
            return {...state, results: {...state.results, status: state.results.next_page > 1 ? Search.SearchHttpStatus.DONE : Search.SearchHttpStatus.INITIAL }};

        case LibraryActionType.LoadResultsSuccess:
            let status = action.versions.length < state.search.limit? Search.SearchHttpStatus.FINAL : Search.SearchHttpStatus.DONE;
            // check if we set current versions to load feedback according access
            let current =  (state.version_feedbacks.feedbackAccess != null && state.version_feedbacks.feedbackAccess == false) ? [] : action.versions;
            switch(state.results.next_page ) {
                case 1:
                    return {...state, 
                        results: {...state.results, items: action.versions, next_page: 2, status: status },
                        version_feedbacks: { ...state.version_feedbacks, current_loading: current } // we set currently loading versions, in case of feebacks
                    };
                default:
                    return {
                        ...state, 
                        results: {
                            ...state.results, 
                            items: [...state.results.items.filter((v: Objects.Version) => !action.versions.some((vv: Objects.Version) => v.id == vv.id)), ...action.versions], 
                            next_page: state.results.next_page + 1, 
                            status: status
                        },
                        version_feedbacks: { ...state.version_feedbacks, current_loading: current }
                    };
            }

        case LibraryActionType.LoadVersionFeedbacks:
            if (state.version_feedbacks.feedbackAccess != null && state.version_feedbacks.feedbackAccess == false){
                return state;
            }

            return {...state, version_feedbacks:  {
                ...state.version_feedbacks,
                    status : Search.SearchHttpStatus.LOADING // 2
                }
            }
        
        case LibraryActionType.LoadVersionFeedbacksSuccess:
            switch(state.results.next_page ) {
                case 1:
                    return {...state, version_feedbacks:  {
                        ...state.version_feedbacks,  
                            feedbacks: action.feedbacks,
                            current_loading: initialState.version_feedbacks.current_loading,
                            status : Search.SearchHttpStatus.DONE // 3
                        }
                    }
                default:
                    return {
                        ...state, 
                        version_feedbacks: {
                            ...state.version_feedbacks , 
                            feedbacks: 
                            [...state.version_feedbacks.feedbacks.filter((f: VersionShareItem) => !action.feedbacks.some((vf: VersionShareItem) => (f.version.id == vf.version.id) )), ...action.feedbacks],
                            current_loading: initialState.version_feedbacks.current_loading,
                            status : Search.SearchHttpStatus.DONE // 3
                        }
                    };
            };
        // When a version 
        case LibraryActionType.LoadVersionEmptyFeedbacksSuccess:
            return {
                ...state, 
                version_feedbacks: {
                    ...state.version_feedbacks , 
                    feedbacks: 
                    [...state.version_feedbacks.feedbacks.filter((f: VersionShareItem) => !action.versions.some((vv: Objects.Version) => (f.version.id == vv.id) ))],
                    current_loading: initialState.version_feedbacks.current_loading,
                    status : Search.SearchHttpStatus.DONE // 3
                }
            };

        case LibraryActionType.ClearVersionFeedbacks:
            return {...state, version_feedbacks: {
                    ...state.version_feedbacks,  
                        feedbacks: initialState.version_feedbacks.feedbacks,
                        status : initialState.version_feedbacks.status //
                    } 
                };
    
        case LibraryActionType.ReloadResults:
            return {...state, results: {...state.results, next_page: 1}};
        

        default:
           return state;
    }
}
